import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FooterNewsletterForm } from './FooterNewsletterForm';

export function LandingFooter({ homePath = '/' }: { homePath?: string } = {}) {
  const { t } = useTranslation();

  const quickLinks = [
    { to: homePath, labelKey: 'landing.nav.home' },
    { to: `${homePath}#courses`, labelKey: 'landing.nav.courses' },
    { to: `${homePath}#teachers`, labelKey: 'landing.nav.teachers' },
    { to: `${homePath}#pricing`, labelKey: 'landing.nav.pricing' },
    { to: `${homePath}#mobile-app`, labelKey: 'landing.footer.quickLinks.app' },
    { to: '/essai-gratuit', labelKey: 'landing.footer.quickLinks.signup' },
  ];

  const aboutLinks = [
    { to: '/a-propos', labelKey: 'landing.footer.aboutLink' },
    { to: '/avis', labelKey: 'landing.footer.reviewsLink' },
    { to: '/faq', labelKey: 'landing.footer.faqLink' },
    { to: '/blog', labelKey: 'landing.footer.blogLink' },
    { to: '/devenir-professeur', labelKey: 'landing.footer.teacherApplicationLink' },
  ];

  return (
    <footer className="border-t border-manuscript-gold-400/50 bg-manuscript-ink font-manuscript">
      <div className="mx-auto max-w-6xl px-6 py-16">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Link to={homePath} className="block">
              <img
                src="/logo.png"
                alt={t('common.appName')}
                className="h-32 w-32 rounded-2xl bg-paper-100 object-contain p-2 shadow-lg sm:h-36 sm:w-36"
              />
            </Link>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-paper-100/65">
              {t('landing.footer.description')}
            </p>
            <div className="mt-5 flex items-center gap-3">
              <a
                href="https://www.facebook.com/share/1b4feydvFU/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('landing.footer.facebookAriaLabel')}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-paper-100/10 text-paper-100/75 transition-colors hover:bg-paper-100/20 hover:text-paper-100"
              >
                <svg viewBox="0 0 24 24" className="h-4.5 w-4.5 fill-current" aria-hidden="true">
                  <path d="M22 12.06C22 6.505 17.523 2 12 2S2 6.505 2 12.06c0 5.02 3.657 9.184 8.438 9.94v-7.03H7.898v-2.91h2.54V9.845c0-2.507 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.242 0-1.63.771-1.63 1.562v1.878h2.773l-.443 2.91h-2.33V22c4.78-.756 8.437-4.92 8.437-9.94Z" />
                </svg>
              </a>
              <a
                href="https://www.instagram.com/go.lugati/?utm_source=qr&r=nametag"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('landing.footer.instagramAriaLabel')}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-paper-100/10 text-paper-100/75 transition-colors hover:bg-paper-100/20 hover:text-paper-100"
              >
                <svg viewBox="0 0 24 24" className="h-4.5 w-4.5 fill-current" aria-hidden="true">
                  <path d="M12 2c-2.72 0-3.06.012-4.123.06-1.062.05-1.79.218-2.427.465a4.9 4.9 0 0 0-1.771 1.153A4.9 4.9 0 0 0 2.525 5.45c-.247.637-.415 1.365-.465 2.427C2.012 8.94 2 9.28 2 12s.012 3.06.06 4.123c.05 1.062.218 1.79.465 2.427a4.9 4.9 0 0 0 1.153 1.771 4.9 4.9 0 0 0 1.771 1.153c.637.247 1.365.415 2.427.465C8.94 21.988 9.28 22 12 22s3.06-.012 4.123-.06c1.062-.05 1.79-.218 2.427-.465a4.9 4.9 0 0 0 1.771-1.153 4.9 4.9 0 0 0 1.153-1.771c.247-.637.415-1.365.465-2.427.048-1.063.06-1.403.06-4.123s-.012-3.06-.06-4.123c-.05-1.062-.218-1.79-.465-2.427a4.9 4.9 0 0 0-1.153-1.771A4.9 4.9 0 0 0 18.55 2.525c-.637-.247-1.365-.415-2.427-.465C15.06 2.012 14.72 2 12 2Zm0 1.802c2.67 0 2.987.01 4.042.059.976.045 1.505.207 1.858.344.467.182.8.399 1.15.748.35.35.566.683.748 1.15.137.353.3.882.344 1.858.048 1.055.058 1.372.058 4.042s-.01 2.987-.058 4.042c-.045.976-.207 1.505-.344 1.858a3.1 3.1 0 0 1-.748 1.15c-.35.35-.683.566-1.15.748-.353.137-.882.3-1.858.344-1.055.048-1.372.058-4.042.058s-2.987-.01-4.042-.058c-.976-.045-1.505-.207-1.858-.344a3.1 3.1 0 0 1-1.15-.748 3.1 3.1 0 0 1-.748-1.15c-.137-.353-.3-.882-.344-1.858-.048-1.055-.059-1.372-.059-4.042s.01-2.987.059-4.042c.045-.976.207-1.505.344-1.858.182-.467.399-.8.748-1.15.35-.35.683-.566 1.15-.748.353-.137.882-.3 1.858-.344 1.055-.048 1.372-.059 4.042-.059Zm0 3.064a5.134 5.134 0 1 0 0 10.268 5.134 5.134 0 0 0 0-10.268Zm0 8.468a3.334 3.334 0 1 1 0-6.668 3.334 3.334 0 0 1 0 6.668Zm6.538-8.671a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0Z" />
                </svg>
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-manuscript-gold-400">
              {t('landing.footer.quickLinksTitle')}
            </h3>
            <ul className="mt-4 space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-paper-100/75 hover:text-paper-100">
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/login" className="text-sm text-paper-100/75 hover:text-paper-100">
                  {t('landing.nav.login')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-manuscript-gold-400">
              {t('landing.nav.courses')}
            </h3>
            <ul className="mt-4 space-y-2.5">
              <li>
                <Link to="/courses" className="text-sm text-paper-100/75 hover:text-paper-100">
                  {t('landing.footer.courses.quran')}
                </Link>
              </li>
              <li>
                <Link to="/courses" className="text-sm text-paper-100/75 hover:text-paper-100">
                  {t('landing.footer.courses.arabic')}
                </Link>
              </li>
              <li>
                <Link to="/courses" className="text-sm text-paper-100/75 hover:text-paper-100">
                  {t('landing.footer.courses.islamicSciences')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-manuscript-gold-400">
              {t('landing.footer.aboutTitle')}
            </h3>
            <ul className="mt-4 space-y-2.5">
              {aboutLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-paper-100/75 hover:text-paper-100">
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-paper-100/75">{t('landing.footer.contactAvailability')}</p>
            <Link
              to="/contact"
              className="mt-3 inline-block text-sm text-manuscript-gold-400 hover:text-manuscript-gold-300"
            >
              {t('landing.footer.writeToUs')} →
            </Link>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center gap-3 border-t border-paper-100/10 pt-8 sm:flex-row sm:justify-between">
          <p className="text-sm text-paper-100/70">{t('landing.footer.newsletter.subtitle')}</p>
          <FooterNewsletterForm />
        </div>

        <p className="mt-8 border-t border-paper-100/10 pt-6 text-center text-xs text-paper-100/75">
          © {new Date().getFullYear()} {t('common.appName')}. {t('landing.footer.rightsReserved')}{' '}
          <Link to="/mentions-legales" className="underline hover:text-paper-100/80">
            {t('landing.footer.legalNoticeLink')}
          </Link>
          {' · '}
          <Link to="/confidentialite" className="underline hover:text-paper-100/80">
            {t('landing.footer.privacyLink')}
          </Link>
          {' · '}
          <Link to="/cgu" className="underline hover:text-paper-100/80">
            {t('landing.footer.termsLink')}
          </Link>
        </p>
      </div>
    </footer>
  );
}
